import type { Partner } from "@expert/shared-types";

/**
 * Maps a raw partner/client signal — an Amazon Connect contact attribute or a CSD
 * `PARTNER`/`CLIENT` code — to a canonical partner key.
 *
 * Returns `undefined` for absent OR blank input (`""`, whitespace) so a caller's
 * `?? fallback` chain engages. Previously a blank value was returned verbatim, which
 * put `partner:""` on the eligibility request and produced a Zod 400 — see the
 * Verizon voice empty-`PARTNER` incident (2026-08-05), where Connect began emitting
 * an empty-string `PARTNER` attribute while the CSD `CLIENT` code (`HZNVZN`) was still
 * populated.
 */
export default function normalizePartner(value: string | undefined): Partner | undefined {
    const normalized = value?.trim().toLowerCase();
    switch (normalized) {
        case "at&t":
            return "att";
        case "vzw":
        case "vzn":
        case "hznvzn": // CSD CLIENT code for Verizon
            return "verizon";
        case "mcafee":
            // The retention IVR stamps PARTNER="mcafee", but standard ExWo has no `mcafee`
            // partner — the retention product ("PC Tech Care by Asurion") runs under
            // `mcafeetechsupport`. (The legacy `mcafee` partner and its overrides live only
            // in the frozen McAfee SaaS env, which never receives this code; cleanup tracked
            // in expert-workspace#6833.) Map the incoming signal onto the configured partner.
            return "mcafeetechsupport";
        default:
            return normalized || undefined;
    }
}
